import { Link } from "react-router-dom";

/**
 * Animated KV International monogram.
 *  - Outer ring: continuous slow rotation (reversible on hover)
 *  - Inner ring: slow reverse rotation
 *  - Curved "KV INTERNATIONAL / CLASSIC · SPORTCARS" text spins with the ring
 *  - KV letters fade + slide-up on mount
 *  - Gold on hover (transitions to #C8A75A), otherwise white
 *
 * Sizes: pass a Tailwind size class like "w-10 h-10" (default) or "w-24 h-24".
 */
export default function Logo({ size = "w-11 h-11", withText = true, asLink = true, className = "" }) {
  const svg = (
    <span
      data-testid="kv-logo"
      className={`kv-logo group inline-flex items-center gap-3 ${className}`}
    >
      <span className={`kv-logo-wrap relative ${size} shrink-0`}>
        <svg viewBox="0 0 200 200" className="w-full h-full text-white group-hover:text-[#C8A75A] transition-colors duration-500">
          <defs>
            <path id="kv-curve-bottom" d="M 22 100 A 78 78 0 0 0 178 100" />
            <path id="kv-curve-top" d="M 30 100 A 70 70 0 0 1 170 100" />
          </defs>

          {/* Outer ring — rotates */}
          <g className="kv-ring-outer">
            <circle cx="100" cy="100" r="92" stroke="currentColor" strokeWidth="1.2" fill="none" opacity="0.9" />
            {/* Ring reveal dashes */}
            <circle cx="100" cy="100" r="92" stroke="currentColor" strokeWidth="1.5" fill="none"
              strokeDasharray="10 578" strokeLinecap="round" opacity="0.9" />
          </g>

          {/* Inner ring — rotates opposite */}
          <g className="kv-ring-inner">
            <circle cx="100" cy="100" r="86" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.5" />
            <line x1="18" y1="100" x2="30" y2="100" stroke="currentColor" strokeWidth="0.8" />
            <line x1="170" y1="100" x2="182" y2="100" stroke="currentColor" strokeWidth="0.8" />
          </g>

          {/* Curved text — rotates with outer ring */}
          <g className="kv-text-ring">
            <text fontFamily="'Montserrat', sans-serif" fontWeight="600" fontSize="10" fill="currentColor" letterSpacing="3" textAnchor="middle">
              <textPath href="#kv-curve-bottom" startOffset="50%">KV INTERNATIONAL</textPath>
            </text>
            <text fontFamily="'Montserrat', sans-serif" fontWeight="400" fontSize="7" fill="currentColor" letterSpacing="4.5" textAnchor="middle" opacity="0.85">
              <textPath href="#kv-curve-top" startOffset="50%">CLASSIC · SPORTCARS</textPath>
            </text>
          </g>

          {/* KV monogram — reveal on mount */}
          <g className="kv-mono" fill="currentColor">
            <path className="kv-letter kv-letter-k" d="M 60 62 L 74 62 L 74 92 L 92 62 L 108 62 L 88 94 L 110 138 L 94 138 L 78 106 L 74 112 L 74 138 L 60 138 Z" />
            <path className="kv-letter kv-letter-v" d="M 108 62 L 122 62 L 132 108 L 142 62 L 156 62 L 142 138 L 122 138 Z" opacity="0.92" />
          </g>
        </svg>

        {/* Subtle radial glow behind logo — reveals on hover */}
        <span aria-hidden className="kv-glow" />
      </span>

      {withText && (
        <span className="text-white/85 text-[10px] uppercase tracking-[0.35em] hidden sm:block font-brand">
          International
        </span>
      )}
    </span>
  );

  if (asLink) return <Link to="/" aria-label="KV International" className="inline-flex">{svg}</Link>;
  return svg;
}
